import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Leer } from '../components/hilfe/Tipp';
import { useMerkZustand } from '../hooks/useMerkZustand';
import { setArbeitsMandant } from '../hooks/useArbeitszeit';
import { Navigate } from 'react-router-dom';
import { Loader2, Inbox } from 'lucide-react';
import Header from '../components/Header';
import { eingangAktualisieren } from '../components/kanzlei/KanzleiLeiste';
import { EingangEintrag } from '../components/kanzlei/EingangEintrag';
import { AbwesendKarte } from '../components/kanzlei/AbwesendKarte';
import { useAuth, api } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { OffeneRechnungenKarte } from '../components/kanzlei/OffeneRechnungenKarte';
import { AkontoOhneRechnung } from '../components/stammakte/HonorarKarte';
import { WochenDashboard, Abschnitt, UnterlagenPruefen } from '../components/kanzlei/WochenDashboard';
import { NeuMenu } from '../components/kanzlei/NeuMenu';

const FILTER = [['fuermich', 'Meine und offene'], ['meine', 'Nur meine'], ['offen', 'Nicht zugewiesen'], ['alle', 'Alle']];
const fehler = (e) => e?.response?.data?.detail || 'Aktion fehlgeschlagen';

const Kennzahlen = ({ counts, filter, setFilter, freelancer }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" role="group" aria-label="Zuständigkeit">
    {FILTER.filter(([k]) => !freelancer || k === 'meine').map(([k, label]) => (
      <button key={k} type="button" onClick={() => setFilter(k)} aria-pressed={filter === k} data-testid={`eingang-filter-${k}`} data-tipp={{ fuermich: 'Ihre Einträge und alles, was noch niemandem zugewiesen ist', meine: 'Nur Einträge, für die Sie zuständig sind', offen: 'Einträge ohne zuständige Person – mit „Übernehmen“ zu Ihrer Aufgabe machen', alle: 'Alle offenen Einträge der Kanzlei' }[k]}
        className={`relative overflow-hidden text-left rounded-2xl border px-5 py-4 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 ${filter === k ? 'border-[#b8893d] bg-[#1B3A5C] text-white shadow-[0_8px_30px_-12px_rgba(27,58,92,0.6)]' : 'border-gray-200 bg-white text-[#1B3A5C] hover:border-[#b8893d]/60'}`}>
        <span className="pointer-events-none absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[radial-gradient(circle,rgba(224,181,106,0.35),transparent_70%)]" />
        <div className={`text-[11px] tracking-[0.2em] uppercase ${filter === k ? 'text-[#e0b56a]' : 'text-gray-500'}`}>{label}</div>
        <div className="font-serif text-4xl mt-1" data-testid={`eingang-anzahl-${k}`}>{counts?.[k] ?? 0}</div>
      </button>
    ))}
  </div>
);

const TypChips = ({ typen, typ, setTyp, total }) => (
  <div className="flex flex-wrap gap-2" role="group" aria-label="Eingang filtern">
    {[['', 'Alle', total], ...Object.entries(typen).map(([t, n]) => [t, t, n])].map(([v, label, n]) => (
      <button key={v || 'alle'} type="button" onClick={() => setTyp(v)} aria-pressed={typ === v} data-testid={`eingang-typ-${v || 'alle'}`} data-tipp={v ? `Nur „${v}“ anzeigen` : 'Alle Arten anzeigen'}
        className={`h-8 px-3 rounded-full text-xs border transition-colors duration-200 ${typ === v ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200 hover:border-[#b8893d]'}`}>
        {label} <b className="ml-1">{n}</b>
      </button>
    ))}
  </div>
);

export default function KanzleiEingang() {
  const { user } = useAuth();
  const { toast } = useToast();
  const freelancer = user?.role === 'freelancer';
  const [filter, setFilter] = useMerkZustand('eingang-filter', freelancer ? 'meine' : 'fuermich');
  const [typ, setTyp] = useMerkZustand('eingang-typ', '');
  const [data, setData] = useState(null);
  const [ladefehler, setLadefehler] = useState('');
  const [team, setTeam] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => api.get('/kanzlei/eingang', { params: { filter } }).then((r) => { setData(r.data); setLadefehler(''); }).catch((e) => setLadefehler(fehler(e))), [filter]);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { api.get('/kanzlei/team').then((r) => setTeam(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setInterval(() => { if (!document.hidden) setTick((x) => x + 1); }, 60000); return () => clearInterval(t); }, []);

  const typen = useMemo(() => (data?.items || []).reduce((a, i) => ({ ...a, [i.typ]: (a[i.typ] || 0) + 1 }), {}), [data]);
  const items = (data?.items || []).filter((i) => !typ || i.typ === typ);

  const run = async (item, fn) => {
    if (item?.mandanten_nr) setArbeitsMandant(item.mandanten_nr);
    setBusyKey(item.key);
    try { await fn(); } catch (e) { toast({ title: fehler(e), variant: 'destructive' }); } finally { setBusyKey(null); setTick((x) => x + 1); eingangAktualisieren(); }
  };
  const aktion = (item, art, extra = {}) => run(item, () => api.post('/kanzlei/eingang/aktion', { key: item.key, aktion: art, version: item.version, ...extra }));
  const uebernehmenFallsFrei = async (item) => { if (!item.zustaendig) await api.post('/kanzlei/eingang/aktion', { key: item.key, aktion: 'uebernehmen', version: item.version }); };
  const verknuepfen = (item, nr) => run(item, async () => {
    await uebernehmenFallsFrei(item);
    if (nr === null) { if (window.confirm('Registrierung ablehnen?')) await api.post(`/admin/registrierungen/${item.ref}/ablehnen`); return; }
    try { await api.post(`/admin/registrierungen/${item.ref}/verknuepfen`, { mandanten_nr: nr }); }
    catch (e) {
      const d = e?.response?.data?.detail || '';
      if (!String(d).startsWith('ABWEICHENDE_EMAIL:')) throw e;
      const [, mail, name] = d.split(':');
      if (!window.confirm(`Mandant ${nr} (${name}) hat die E-Mail ${mail}. Trotzdem verknüpfen?`)) return;
      await api.post(`/admin/registrierungen/${item.ref}/verknuepfen`, { mandanten_nr: nr, abweichende_email_bestaetigt: true });
    }
    toast({ title: `Mit Mandant ${nr} verknüpft` });
  });
  const posten = (item, url) => run(item, async () => {
    await uebernehmenFallsFrei(item);
    const { data: d } = await api.post(url);
    const dt = (v) => new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const info = d?.bezahlt ? 'Rate wurde eingezogen' : d?.hinweis || (d?.geplant_am ? `Erneuter Einzug am ${dt(d.geplant_am)}` : d?.faellig_am ? `Neue Lastschrift zum ${dt(d.faellig_am)} vorbereitet` : 'erledigt');
    toast({ title: `${url === item.post2_url ? item.aktion2_label : item.aktion_label}: ${info}` });
  });
  const erinnern = (item) => run(item, async () => {
    await uebernehmenFallsFrei(item);
    await api.post(`/admin/esign/${item.ref}/senden`);
    const r = await api.get('/kanzlei/eingang', { params: { filter: 'alle' } });
    const akt = (r.data.items || []).find((i) => i.key === item.key);
    if (akt) await api.post('/kanzlei/eingang/aktion', { key: item.key, aktion: 'erinnert', version: akt.version });
    toast({ title: 'Erinnerung versendet' });
  });

  if (!['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return (
    <div className="min-h-screen bg-[#f5f8fa] bg-[radial-gradient(circle_at_1px_1px,rgba(27,58,92,0.07)_1px,transparent_0)] [background-size:22px_22px]" data-testid="kanzlei-eingang">
      <Header />
      <div className="pt-[104px] lg:pt-[152px]">
        <main className="max-w-[1500px] mx-auto px-4 lg:px-8 py-[26px] space-y-6">
          <NeuMenu leiste />
          <WochenDashboard freelancer={freelancer}>
          {!freelancer && <AkontoOhneRechnung kompakt />}
          {!freelancer && <UnterlagenPruefen />}
          <Abschnitt titel="Eingang · alles, was eine Reaktion braucht (jeder Eintrag hat eine zuständige Person)" testId="dash-eingang">
          <div className="space-y-3">
          <Kennzahlen counts={data?.counts} filter={filter} setFilter={setFilter} freelancer={freelancer} />
          {data && items.length > 0 && <TypChips typen={typen} typ={typ} setTyp={setTyp} total={data.items.length} />}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-[0_10px_40px_-24px_rgba(11,23,36,0.35)]">
            {ladefehler && !data ? <div className="p-10 text-center text-sm text-rose-700" data-testid="eingang-fehler">{ladefehler}</div>
              : !data ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>
              : !items.length ? <div className="p-4"><Leer icon={Inbox} testId="eingang-leer" titel="Alles erledigt – der Eingang ist leer" text={`Neue Nachrichten, Dokumente, Registrierungen, Rückrufwünsche, Zustimmungen und überfällige Zahlungen erscheinen hier automatisch.${freelancer ? '' : ' Tipp: Über die Kacheln oben sehen Sie auch Einträge anderer oder nicht zugewiesene.'}`} /></div>
                : <ul className="divide-y divide-gray-100" data-testid="eingang-liste">
                  {items.map((i) => <EingangEintrag key={i.key} item={i} me={user.user_id} istAdmin={user.role === 'admin'} istFreelancer={freelancer} team={team} busy={busyKey === i.key}
                    onAktion={(art, extra) => aktion(i, art, extra)} onVerknuepfen={(nr) => verknuepfen(i, nr)} onErinnern={() => erinnern(i)} onPost={(url) => posten(i, url)} onRun={(fn, titel) => run(i, async () => { await uebernehmenFallsFrei(i); const r = await fn(); toast({ title: typeof titel === 'function' ? titel(r) : titel }); })} />)}
                </ul>}
          </div>
          </div>
          </Abschnitt>
          </WochenDashboard>
          {!freelancer && <AbwesendKarte />}
          {!freelancer && <OffeneRechnungenKarte />}
          {!freelancer && <p className="text-xs text-gray-500">Registrierungen werden nie automatisch mit einem Mandanten verknüpft. Prüfen Sie vor der Freigabe, ob die Person tatsächlich Ihr Mandant ist.</p>}
        </main>
      </div>
      <Toaster />
    </div>
  );
}
